<template>
  <div :style="gridStyle" class="cargo-ag-grid">
    <ag-grid-vue
      ref="cargoAgGrid"
      :locale-text-func="_i18nGridLocalText"
      :grid-options="gridOptions"
      :row-data="rowData"
      :side-bar="showSideBar?defaultSideBar:null"
      :get-main-menu-items="getMainMenuItems"
      :process-cell-for-clipboard="onProcessCellForClipboard"
      :row-height="gridOptions.rowHeight||29"
      :allow-drag-from-columns-tool-panel="true"
      :enable-browser-tooltips="true"
      :style="`width: 100%;flex:1;min-height: ${minHeight}`"
      class="ag-theme-balham"
      :class="[showSelectedColor?'showSelectedColor':'']"
      @cell-context-menu="cellEventCallback"
      @grid-ready="onReady"
      @row-drag-end="rowDragEndCallback"
      @cell-clicked="cellEventCallback"
      @displayedColumnsChanged="onGridDisplayedColumnsChanged"
      @columnResized="onGridColumnsChanged"
      @row-data-changed="onRowDataChanged"
      @row-data-updated="onRowDataUpdated"
      @cellValueChanged="onCellValueChanged"
      @filter-changed="onFilterChanged"
      @sort-changed="onSortChanged"
      @cell-focused="onCellFocused"
      @rowGroupOpened="onRowGroupOpened"
      @expandOrCollapseAll="onExpandOrCollapseAll"
      @columnRowGroupChanged="onColumnRowGroupChanged"
      @selectionChanged="onSelectionChanged"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { getLength } from '@/utils'
import { getUserInfo } from '@/utils/auth'
import { getPersonQueryConfig, savePersonQueryConfig } from '@/api/commonComponent'
import { dataConvert, exportExcel, exportPdf } from '@/utils/tableDataExportUtils'
import ColumnUtils from '@/utils/ColumnUtils'
import { getLoading } from '@/utils/utilBaseElement'
import { repareNodeSort, stringToNum, DocumentManage } from '@/utils/commonUtil'

import ChargeCurrencyTotalGroup from '@/components/Cargo/Act/chargeSummary/ChargeCurrencyTotalGroupHP'
import CargoGridOptCheckBox from '@/components/Cargo/grid/cargoGridOptCheckBox'
import sopEmailOptBtn from '@/components/Cargo/grid/sopEmailOptBtn'
import CargoGridShowBigContent from '@/components/Cargo/grid/cargoGridShowBigContent'
import CargoAgGridNoRowOverlay from '@/components/Cargo/Common/CargoAgGridNoRowsOverLay'
import { AgGridVue } from 'ag-grid-vue'
import i18n from '@/lang'
import { isNumber2 } from '@/utils/validate'

export default {
  name: 'CargoAgGrid',
  components: {
    AgGridVue,
    sopEmailOptBtn,
    CargoGridShowBigContent,
    CargoGridOptCheckBox
  },
  props: {
    configModule: {
      type: String,
      default: ''
    },
    perConfigName: {
      type: String,
      default: ''
    },
    refreshId: {
      type: Number,
      default: 0
    },
    showSideBar: {
      type: Boolean,
      default: true
    },
    debug: {
      type: Boolean,
      default: false
    },
    exportFileName: {
      type: String,
      default: () => null
    },
    gridStyle: {
      type: String,
      default: ''
    },
    minHeight: {
      type: String,
      default: '200px'
    },
    gridParams: {
      type: Object,
      required: false,
      default: () => ({})
    },
    gridOptions: {
      type: Object,
      required: true
    },
    columnDefs: {
      type: Array,
      default: () => null
    },
    sideBar: {
      type: Object,
      required: false,
      default: () => {
        return { toolPanels: ['columns'], visible: false }
      }
    },
    rowData: {
      type: Array,
      default: () => {
        return []
      }
    },
    checkSubRecord: { // 勾选主票时，是否自动勾选小票
      type: Boolean,
      default: () => true
    },
    checkMainRecord: { // 小票全部勾选时，是否自动选中主票
      type: Boolean,
      default: () => true
    },
    deselectMainRecord: { // 取消小票勾选是否取消主票勾选
      type: Boolean,
      default: () => true
    },
    deselectSubRecord: { // 取消主票勾选是否取消小票勾选
      type: Boolean,
      default: () => false
    },
    shiftSelect: { // 可按住Shift键选择
      type: Boolean,
      default: () => false
    },
    ctrlSelect: { // 可按住Ctrl键选择
      type: Boolean,
      default: () => false
    },
    rowIndexName: { // 行序号字段名
      type: String,
      default: () => 'serialNumber'
    },
    totalConfig: { // excel合计配置
      type: Array,
      default: () => null
    },
    wrapText: {
      type: Boolean,
      default: () => false
    },
    showSelectedColor: {
      type: Boolean,
      default: () => true
    },
    hasExportRole: { // 是否有导出Excel权限【默认有】
      type: Boolean,
      default: true
    },
    cancelSortAutoReduce: { // 取消排序后，是否自动缩减(列宽)
      type: Boolean,
      default: true
    },
    preColumnStateCallback: { // 列状态预处理回调方法
      type: Function,
      default: null
    },
    customMenuItems: { // 统计汇总方式
      type: Object,
      default: null
    },
    filterMenuItems: { // 过滤菜单项
      type: Function,
      default: null
    },
    useCacheInit: { // 是否使用缓存初始化
      type: Boolean,
      default: false
    },
    showMenuItems: { // 是否显示菜单项
      type: Boolean,
      default: true
    },
    enableQuickValueConvert: { type: Boolean, default: false } // 是否启用快速值转换
  },
  data() {
    return {
      isComponentAlive: true,
      pageName: '',
      uuid: '',
      params: {},
      defaultSideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressRowGroups: this.gridOptions.rowGroupPanelShow !== 'always',
              suppressValues: true,
              suppressPivots: true,
              suppressPivotMode: true,
              suppressSideButtons: true,
              suppressColumnFilter: false,
              suppressColumnSelectAll: false,
              suppressColumnExpandAll: true
            }
          }
        ],
        visible: true
      },
      perConfig: {
        bDefault: true,
        configContent: '',
        configModule: this.configModule,
        configName: 'default',
        configType: 2, // 固定值，表格配置
        configBy: 0
      },
      defaultColumnState: null,
      oldSortList: [], // 用于存储排序之前的排序状态
      nowSortList: [],
      // currentSort: ''
      currentColumn: '', // 当前列
      shiftFlag: false,
      shiftStartIndex: null,
      ctrlFlag: false,
      ctrlRows: null,
      documentManage: new DocumentManage(),
      useDefaultColumnState: true, // 是否使用默认列状态
      closeToolPanelTimerId: 0, // 关闭“设置列”窗口定时器
      existCustomGroup: this.gridOptions.treeData || this.gridOptions.autoGroupColumnDef, // 是否存在自定义分组
      usedRowGroup: this.gridOptions.rowGroupPanelShow === 'always', // 是否有使用行分组
      columnRowGroupChangedManager: { // 列分组改变管理器
        eventQueue: [], // 事件队列
        isProcessing: false, // 是否正在处理
        processQueue: () => {
          const manager = this.columnRowGroupChangedManager
          if (manager.isProcessing || manager.eventQueue.length === 0) {
            return
          }
          manager.isProcessing = true
          const nextEvent = manager.eventQueue.shift()
          nextEvent()
          setTimeout(() => {
            manager.isProcessing = false
            manager.processQueue()
          }, 100)
        }
      },
      valueConvertMap: new Map() // 快速值转换的映射
    }
  },
  computed: {
    route() {
      return this.$route
    }
  },
  watch: {
    perConfigName() {
      if (!this.isReady) {
        return
      }
      this.refreshColumnState()
    },
    refreshId() {
      if (!this.isReady) {
        return
      }
      this.refreshColumnState()
    },
    columnDefs: {
      // immediate: true,
      handler: function(newVal, oldVal) {
        if (!newVal) {
          return
        }
        if (this.params.api) {
          this.setColumnDefs()
          this.refreshColumnState(true)
        }
        this.updateValueConvertMap()
      },
      deep: true
    },
    rowData() {
      this.updateValueConvertMap()
    },
    enableQuickValueConvert() {
      this.updateValueConvertMap()
    }
  },
  created() {
    this.pageName = this.route.name
    const { gridOptions, treeDataRowSelectedCallBack } = this
    if (!this.gridOptions.hasOwnProperty('unSortIcon')) {
      this.gridOptions.unSortIcon = false
    }
    if (!this.gridOptions.hasOwnProperty('suppressCopyRowsToClipboard')) {
      this.gridOptions.suppressCopyRowsToClipboard = true
    }
    if (!this.gridOptions.hasOwnProperty('suppressColumnVirtualisation')) {
      this.gridOptions.suppressColumnVirtualisation = false
    }
    if (!this.gridOptions.hasOwnProperty('suppressClipboardPaste')) {
      this.gridOptions.suppressClipboardPaste = true
    }
    if (!this.gridOptions.hasOwnProperty('rowDragManaged')) {
      this.gridOptions.rowDragManaged = true
    }
    if (!this.gridOptions.hasOwnProperty('suppressScrollOnNewData')) {
      this.gridOptions.suppressScrollOnNewData = true
    }
    if (!this.gridOptions.hasOwnProperty('suppressAnimationFrame')) {
      this.gridOptions.suppressAnimationFrame = true
    }
    if (!this.gridOptions.hasOwnProperty('rowBuffer')) {
      this.gridOptions.rowBuffer = 50
    }

    this.gridOptions.noRowsOverlayComponentFramework = CargoAgGridNoRowOverlay
    this.gridOptions.getContextMenuItems = gridOptions.getContextMenuItems || this.getContextMenuItems
    const customDefaultColDef = gridOptions.defaultColDef ? gridOptions.defaultColDef : {}
    this.gridOptions.defaultColDef = {
      minWidth: 100,
      width: 100,
      sortable: true,
      resizable: true,
      wrapText: this.wrapText,
      autoHeight: this.wrapText,
      menuTabs: ['generalMenuTab', 'filterMenuTab'],
      filter: true,
      /* headerClass: (params) => {
        const { colDef: { headerName, checkboxSelection }} = params
        if (!headerName && checkboxSelection) {
          return 'checkboxSelection'
        }
      },*/
      tooltipValueGetter: (params) => {
        if (params.valueFormatted) {
          return params.valueFormatted
        }
        if (params.value && params.value.length > 20) {
          return params.value
        }
      },
      ...customDefaultColDef
    }
    if (!this.gridOptions.hasOwnProperty('suppressRowClickSelection')) {
      this.gridOptions.suppressRowClickSelection = true
    }
    if (!this.gridOptions.hasOwnProperty('suppressContextMenu')) {
      this.gridOptions.suppressContextMenu = !this.configModule
    }
    if (gridOptions.treeData) {
      const userRowSelected = gridOptions.onRowSelected || null
      this.gridOptions.onRowSelected = async(params) => {
        await treeDataRowSelectedCallBack(params)
        userRowSelected && userRowSelected(params)
      }
    }
    this.updateValueConvertMap()
  },
  activated() {
    if (Object.keys(this.params).length) {
      const { api } = this.params
      const focusedCell = api.getFocusedCell()
      if (focusedCell) {
        api.ensureIndexVisible(focusedCell.rowIndex)
        /* const elPinned = document.querySelector('.ag-pinned-left-cols-viewport')
        const el = document.querySelector('.ag-body-viewport.ag-layout-normal')
        elPinned.scrollTop = el.scrollTop*/
      }
    }
  },
  mounted() {
    this.documentManage.set('onKeydown', e => {
      this.onKeydown(e)
    })
    this.documentManage.set('onKeyup', e => {
      this.onKeyup(e)
    })
    window.addEventListener('keydown', this.documentManage.get('onKeydown'), false)
    window.addEventListener('keyup', this.documentManage.get('onKeyup'), false)
  },
  beforeDestroy() {
    this.isComponentAlive = false
    window.removeEventListener('beforeunload', this.saveState)
    window.removeEventListener('keydown', this.documentManage.get('onKeydown'))
    window.removeEventListener('keyup', this.documentManage.get('onKeyup'))
    window.removeEventListener('click', this.clickToolPanelOther)
    if (this.params && this.params.api && typeof this.params.api.destroy === 'function') {
      this.params.api.destroy()
    }
    this.documentManage.destory()
  },
  methods: {
    isGridUsable() {
      return this.isComponentAlive && !!(this.params && this.params.api)
    },
    getGridRootEl() {
      const cargoAgGridRef = this.$refs.cargoAgGrid
      return cargoAgGridRef && cargoAgGridRef.$el ? cargoAgGridRef.$el : null
    },
    onSortChanged() {
      const currentColumn = this.currentColumn
      this.currentColumn = '' // 将当前列置空，避免后续手动刷新列状态时（会触发onSortChanged），使用上次的值
      const nowList = []
      this.nowSortList = this.params.columnApi.getColumnState()
      this.nowSortList.forEach(eve => {
        if (currentColumn) {
          if (eve.colId === currentColumn) {
            nowList.push(eve)
          }
        } else if (!currentColumn && eve.sort !== null) {
          nowList.push(eve)
        }
      })
      if (this.oldSortList.length === 0) {
        this.oldSortList = nowList
      } else {
        let hasOld = false
        this.oldSortList.forEach(old => {
          if (nowList[0] && old.colId === nowList[0].colId) {
            old.sort = nowList[0].sort
            hasOld = true
          }
        })
        if (!hasOld && nowList[0]) {
          this.oldSortList.push({
            colId: nowList[0].colId,
            sort: nowList[0].sort,
            sortIndex: nowList[0].sortIndex
          })
        }
      }

      if (nowList.length === 1) {
        this.oldSortList.forEach((eve, index) => {
          eve.sortIndex = index
        })
      }

      const columnState = this.params.columnApi.getColumnState()
      columnState.forEach(state => {
        this.oldSortList.find(old => {
          if (old.colId === state.colId) {
            state.sort = old.sort
            state.sortIndex = old.sortIndex
            if (state.sort) {
              const headerWidth = this.getHeaderWidth(state)
              if (state.width < headerWidth) {
                state.width = headerWidth
              }
            } else if (state.colId === currentColumn) {
              if (this.cancelSortAutoReduce) {
                const headerWidth = this.getHeaderWidth(state)
                if (state.width > headerWidth) {
                  let newWidth = state.width - 20
                  if (newWidth < headerWidth) {
                    newWidth = headerWidth
                  }
                  state.width = newWidth
                }
              }
            }
          }
        })
      })
      // this.params.columnApi.setColumnState(columnState)
      this.setColumnWidthAndState(columnState)
      this.oldSortList.forEach((eve, index) => {
        if (eve.sort === null) {
          this.oldSortList.splice(index, 1)
        }
      })
      this.$emit('sort-changed', this.oldSortList)
    },
    getHeaderWidth(state) {
      const columnDefs = []
      if (this.gridOptions.autoGroupColumnDef) {
        let autoGroupColumnDef = this.gridOptions.autoGroupColumnDef
        if (!autoGroupColumnDef.colId) {
          autoGroupColumnDef = { ...autoGroupColumnDef, colId: autoGroupColumnDef.field }
        }
        columnDefs.push(autoGroupColumnDef)
        columnDefs.push({ ...autoGroupColumnDef, colId: 'ag-Grid-AutoColumn' })
      }
      if (this.columnDefs) {
        columnDefs.push(...this.columnDefs)
      }
      const columnDef = columnDefs.find(p => p.colId === state.colId)
      if (columnDef && columnDef.headerName) {
        let len = 42 // 固定宽度
        len += getLength(columnDef.headerName + (typeof state.sortIndex === 'number' ? String(state.sortIndex + 1) : '')) * 0.5 * 12 // 文本字节数 * 0.5 * font-size
        for (let i = 0; i < columnDef.headerName.length; ++i) {
          const c = columnDef.headerName.charAt(i)
          if (c >= 'A' && c <= 'Z') { // 大写字母占用的宽度会比标准大一点
            len += 1.8
          } else if (c === '（' || c === '）') {
            len += 4.2
          }
        }
        if (state.sort) {
          len += 20 // 排序宽度
        }
        return len
      }
      return 0
    },
    setColumnWidthAndState(columnState) {
      if (!this.isGridUsable()) {
        return
      }
      const columnDefs = lodash.cloneDeep(this.columnDefs)
      for (const columnDef of columnDefs) {
        for (const state of columnState) {
          if (state.colId === columnDef.colId) {
            columnDef.width = state.width
            break
          }
        }
      }
      this.params.api.setColumnDefs(columnDefs)
      this.params.columnApi.setColumnState(columnState)
    },
    // 筛选面板-筛选选项发生改变
    onFilterChanged(params) {
      params.api.clearFocusedCell()
      params.api.selectAll()
      params.api.deselectAll()
    },
    onRowDataChanged(params) {
      this.$emit('rowDataChanged', params)
    },
    onRowDataUpdated(params) {
      this.$emit('rowDataUpdated', params)
    },
    onCellValueChanged(params) {
      params.api.resetRowHeights()
      this.$emit('cellValueChanged', params)
    },
    onGridDisplayedColumnsChanged(params) {
      if (!this.isReady) {
        return
      }
      const columnStates = params.columnApi.getColumnState()
      const sortList = [] // 排序列
      let adjustSort = false // 是否需要调整排序
      for (const columnState of columnStates) {
        if (columnState.sort && columnState.sortIndex >= 0) {
          if (columnState.hide) {
            columnState.sort = null
            columnState.sortIndex = null
            adjustSort = true
          } else {
            sortList.push(columnState)
          }
        }
      }
      if (adjustSort) {
        sortList.sort((a, b) => a.sortIndex - b.sortIndex)
        for (let i = 0; i < sortList.length; ++i) {
          sortList[i].sortIndex = i
        }
        this.oldSortList = sortList
        params.columnApi.setColumnState(columnStates)
      }
      this.onGridColumnsChanged(params)
      this.$emit('displayedColumnsChanged', params)
    },
    onGridColumnsChanged(params) {
      if (!this.isReady) {
        return
      }
      if (this.useDefaultColumnState) {
        this.saveLocalSate(params)
        this.$nextTick(() => {
          this.gridChangeDebounce(params)
        })
      } else {
        if (this.configModule) {
          const currentUserInfo = getUserInfo()
          localStorage.setItem(
            `local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}_${this.perConfigName}_temp`,
            JSON.stringify(params.columnApi.getColumnState())
          )
        }
      }
      const grid = this.getGridRootEl()
      if (!grid) {
        return
      }
      const headerContainer = grid.querySelector('.ag-header')
      if (headerContainer) {
        const cells = headerContainer.querySelectorAll('.ag-header-cell')
        const _this = this
        for (const cell of cells) {
          const label = cell.querySelector('.ag-header-cell-label')
          if (label) {
            label.addEventListener('mousedown', function(event) {
              // _this.currentSort = event.target.innerText
              // if (_this.currentSort.indexOf('\n') > -1) {
              //   const len = _this.currentSort.indexOf('\n')
              //   _this.currentSort = _this.currentSort.substring(0, len)
              // }
              _this.currentColumn = cell.getAttribute('col-id')
            })
          }
        }
      }
    },
    gridChangeDebounce: lodash.debounce(function(params) {
      params.api.resetRowHeights()
      this.saveState(params)
    }, 3000, {
      trailing: true,
      maxWait: 6000
    }),
    async treeDataRowSelectedCallBack(p) {
      // console.log('treeRowSelect', p)
      if (!p.data) {
        return
      }
      if (p.node.allChildrenCount) {
        if (p.node.selected && this.checkSubRecord) {
          p.node.childrenAfterGroup.forEach((node) => {
            node.setSelected(p.node.selected)
          })
        } else {
          if (this.deselectSubRecord) {
            const isTrue = p.node.childrenAfterGroup.filter(
              (node) => node.selected
            )
            if (isTrue.length === p.node.childrenAfterGroup.length) {
              p.node.childrenAfterGroup.forEach((node) => {
                node.setSelected(p.node.selected)
              })
            }
          }
        }
      } else {
        if (!p.node.selected) {
          if (p.node.parent.selected && this.deselectMainRecord) {
            p.node.parent.setSelected(false)
          }
        } else {
          const brothers = p.node.parent.childrenAfterGroup
          const selected = brothers.filter((node) => node.selected)
          if (selected.length === brothers.length && this.checkMainRecord) {
            p.node.parent.setSelected(true)
          }
        }
      }
    },
    async onReady(params) {
      // params.api.sizeColumnsToFit()
      this.params = params
      this.setColumnDefs()
      if (this.configModule) {
        this.setInitColumnStatus(params)
        await this.getColumnStatus()
      } else {
        this.refreshColumnState()
      }
      if (!this.isComponentAlive || this.params !== params) {
        return
      }
      this.$emit('update:useCacheInit', false)
      const grid = this.getGridRootEl()
      if (grid && params.api.isSideBarVisible()) {
        const toolPanelWrapper = grid.querySelector('.ag-tool-panel-wrapper')
        const toolPanelInput = grid.querySelector('.ag-column-panel .ag-input-field-input.ag-text-field-input')
        if (toolPanelWrapper) {
          toolPanelWrapper.addEventListener('mouseenter', this.toolPanelEnterListener)
          toolPanelWrapper.addEventListener('mouseleave', this.toolPanelListener)
        }
        if (toolPanelInput) {
          toolPanelInput.addEventListener('compositionstart', this.toolPaneInputStartListener)
          toolPanelInput.addEventListener('compositionend', this.toolPaneInputEndListener)
        }
      }
      // console.log('sidebar', params.api.isSideBarVisible())
      this.isReady = true
      this.$emit('update:gridParams', params)
      this.$emit('gridReady', params)
    },
    onKeydown(e) {
      switch (e.keyCode) {
        case 16:
          if (this.shiftSelect) {
            if (!this.shiftFlag) {
              const cell = this.params.api.getFocusedCell()
              this.shiftStartIndex = cell ? cell.rowIndex : null
            }
            this.shiftFlag = true
          }
          break
        case 17:
          if (this.ctrlSelect) {
            if (!this.ctrlFlag) {
              this.ctrlRows = []
              this.setCtrlSelects()
            }
            this.ctrlFlag = true
          }
          break
      }
    },
    onKeyup(e) {
      switch (e.keyCode) {
        case 16:
          if (this.shiftSelect) {
            this.shiftFlag = false
          }
          break
        case 17:
          if (this.ctrlSelect) {
            this.ctrlFlag = false
          }
          break
      }
    },
    onCellFocused(event) {
      if (this.shiftSelect) {
        if (this.shiftFlag) {
          const cell = this.params.api.getFocusedCell()
          const curIndex = cell.rowIndex
          if (this.shiftStartIndex === null) {
            this.shiftStartIndex = curIndex
          }
          let beginIndex = 0
          let endIndex = 0
          if (this.shiftStartIndex > curIndex) {
            beginIndex = curIndex
            endIndex = this.shiftStartIndex
          } else {
            beginIndex = this.shiftStartIndex
            endIndex = curIndex
          }
          const nodes = this.params.api.rowModel.rowsToDisplay
          const rows = []
          for (let i = beginIndex; i <= endIndex; ++i) {
            rows.push(nodes[i].data)
          }
          /* 设置选中效果 */
          const grid = this.getGridRootEl()
          const agRows = grid ? grid.querySelectorAll('.ag-row') : []
          for (const agRow of agRows) {
            const clazz = agRow.getAttribute('class')
            const rowIndex = Number(agRow.getAttribute('row-index'))
            if (rowIndex >= beginIndex && rowIndex <= endIndex) {
              if (clazz.indexOf(' shift-select') < 0) {
                agRow.setAttribute('class', clazz + ' shift-select')
              }
            } else {
              agRow.setAttribute('class', clazz.replace(' shift-select', ''))
            }
          }
          this.$emit('shiftSelects', rows)
        } else {
          if (this.shiftStartIndex !== null) {
            const grid = this.getGridRootEl()
            const agRows = grid ? grid.querySelectorAll('.ag-row.shift-select') : []
            for (const agRow of agRows) {
              const clazz = agRow.getAttribute('class')
              agRow.setAttribute('class', clazz.replace(' shift-select', ''))
            }
            this.shiftStartIndex = null
            this.$emit('shiftSelects', null)
          }
        }
      }
      if (this.ctrlSelect) {
        if (this.ctrlFlag) {
          this.setCtrlSelects()
          this.$emit('ctrlSelects', this.ctrlRows)
        } else {
          if (this.ctrlRows !== null) {
            const grid = this.getGridRootEl()
            const agRows = grid ? grid.querySelectorAll('.ag-row.ctrl-select') : []
            for (const agRow of agRows) {
              const clazz = agRow.getAttribute('class')
              agRow.setAttribute('class', clazz.replace(' ctrl-select', ''))
            }
            this.ctrlRows = null
            this.$emit('ctrlSelects', null)
          }
        }
      }
    },
    setCtrlSelects() {
      const nodes = this.params.api.rowModel.rowsToDisplay
      const cell = this.params.api.getFocusedCell()
      if (nodes[cell.rowIndex] && !this.ctrlRows.includes(nodes[cell.rowIndex].data)) {
        this.ctrlRows.push(nodes[cell.rowIndex].data)
      }
      /* 设置选中效果 */
      const rowIndexs = []
      for (const node of nodes) {
        if (this.ctrlRows.includes(node.data)) {
          rowIndexs.push(node.rowIndex)
        }
      }
      const grid = this.getGridRootEl()
      const agRows = grid ? grid.querySelectorAll('.ag-row') : []
      for (const agRow of agRows) {
        const clazz = agRow.getAttribute('class')
        const rowIndex = Number(agRow.getAttribute('row-index'))
        if (rowIndexs.includes(rowIndex)) {
          if (clazz.indexOf(' ctrl-select') < 0) {
            agRow.setAttribute('class', clazz + ' ctrl-select')
          }
        } else {
          agRow.setAttribute('class', clazz.replace(' ctrl-select', ''))
        }
      }
    },
    onRowGroupOpened(event) {
      this.$emit('rowGroupOpened', event)
    },
    onSelectionChanged(event) {
      this.$emit('selection-changed', event)
    },
    onExpandOrCollapseAll(event) {
      this.$emit('expandOrCollapseAll', event)
    },
    onColumnRowGroupChanged(event) {
      if (this.usedRowGroup) {
        const columnStates = event.columnApi.getColumnState()
        let autoColumnIndex = -1
        const groupColIdSet = new Set()
        for (const column of event.columns) {
          groupColIdSet.add(column.colId)
        }
        const groupColumnStates = []
        for (let i = columnStates.length - 1; i >= 0; --i) {
          if (columnStates[i].colId === 'ag-Grid-AutoColumn') {
            autoColumnIndex = i
          } else if (columnStates[i].rowGroup) {
            groupColumnStates.push(columnStates[i])
            columnStates.splice(i, 1)
          }
        }
        if (autoColumnIndex > -1) {
          groupColumnStates.sort((a, b) => a.rowGroupIndex - b.rowGroupIndex)
          columnStates.splice(autoColumnIndex + 1, 0, ...groupColumnStates)
          event.columnApi.setColumnState(columnStates)
        }
      }
      if (this.useDefaultColumnState) {
        this.saveLocalSate(event)
      } else {
        if (this.configModule) {
          const currentUserInfo = getUserInfo()
          localStorage.setItem(
            `local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}_${this.perConfigName}_temp`,
            JSON.stringify(event.columnApi.getColumnState())
          )
        }
      }
      const manager = this.columnRowGroupChangedManager
      manager.eventQueue.push(() => {
        this.$emit('columnRowGroupChanged', event)
      })
      manager.processQueue()
    },
    cellEventCallback(params) {
      // console.log('cellClick', params.event.target, params.event.target.nodeName)
      // this.$emit('cell-clicked', params)
      if ((params.event.target.nodeName === 'DIV' || params.event.target.nodeName === 'SPAN') && !params.column.colDef.editable) {
        const selection = document.getSelection()
        if (selection.type !== 'Range' && selection.type !== 'None') {
          const range = document.createRange()
          if (params.event.target.parentNode && params.event.target.parentElement && params.event.target.innerText) {
            range.selectNode(params.event.target)
            selection.removeAllRanges()
            selection.addRange(range)
          }
        }
      }
    },
    autoSizeColumns(params) {
      params.columnApi.autoSizeAllColumns(true)
      this.$nextTick(() => {
        const columnState = params.columnApi.getColumnState()
        const colIds = columnState.filter(item => item.width > 200).map(item => ({ key: item.colId, newWidth: 200 }))
        params.columnApi.setColumnWidths(colIds)
        // if (this.configModule) {
        //   this.saveState()
        // }
      })
    },
    /**
     * 收集统计汇总配置
     * @param val
     */
    setSummaryModesSummaryModes(val) {
      if (this.customMenuItems) {
        const columnSummaryModeDict = {}
        for (const columnDef of this.columnDefs) {
          if (columnDef.children) {
            for (const child of columnDef.children) {
              if (child.summaryMode) {
                columnSummaryModeDict[child.colId || child.field] = child.summaryMode
              }
            }
          } else {
            if (columnDef.summaryMode) {
              columnSummaryModeDict[columnDef.colId || columnDef.field] = columnDef.summaryMode
            }
          }
        }
        const parsedVal = JSON.parse(val || '[]')
        if (Array.isArray(parsedVal)) {
          const newConfigContent = parsedVal.reduce((acc, item) => {
            // 确保 item 包含 colId 和 [this.customMenuItems.summaryModes.key] 属性
            if (item.colId) {
              const key = item.colId
              const value = item[this.customMenuItems.summaryModes.key]
              if (value) {
                acc[key] = value
              } else {
                if (columnSummaryModeDict[key]) {
                  acc[key] = columnSummaryModeDict[key]
                } else {
                  if (!this.customMenuItems.summaryModes.configContent[key]) {
                    acc[key] = 'subtotal'
                  }
                }
              }
            }
            return acc
          }, {})
          if (Object.keys(newConfigContent).length > 0) {
            // 合并新的配置与原有配置
            this.customMenuItems.summaryModes.configContent = {
              ...this.customMenuItems.summaryModes.configContent,
              ...newConfigContent
            }
          }
        }
      }
    },
    async getColumnStatus() {
      if (!this.isGridUsable()) {
        return
      }
      const currentUserInfo = getUserInfo()
      const perConfig = {
        bDefault: true,
        configModule: this.configModule,
        configNamePrefix: `local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`,
        configType: 2, // 固定值，表格配置
        configBy: currentUserInfo.userId,
        tenantId: currentUserInfo.tenantId
      }
      if (this.configModule && !this.debug) {
        if (this.useCacheInit) {
          let defaultColumnState = localStorage.getItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`)
          if (!defaultColumnState) {
            defaultColumnState = localStorage.getItem(`local_uiDoc_init_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`)
          }
          this.defaultColumnState = defaultColumnState
          this.setSummaryModesSummaryModes(this.defaultColumnState)
          // 取方案对应列状态
          if (this.perConfigName) {
            let perColumnState = localStorage.getItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}_${this.perConfigName}_temp`)
            if (!perColumnState) {
              perColumnState = localStorage.getItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}_${this.perConfigName}`)
            }
            if (perColumnState) {
              if (!this.isGridUsable()) {
                return
              }
              this.useDefaultColumnState = false
              this.setColumnWidthAndState(this.handleColumnState(JSON.parse(perColumnState)))
              return
            }
          }
          this.useDefaultColumnState = true
          if (this.defaultColumnState) {
            if (!this.isGridUsable()) {
              return
            }
            this.setColumnWidthAndState(this.handleColumnState(JSON.parse(defaultColumnState)))
          }
        } else {
          const localUpdateTime = localStorage.getItem(`local_uiDoc_person_gridStatus_update_time_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`)
          const localColumnState = localStorage.getItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`)
          await getPersonQueryConfig(perConfig).then(res => {
            if (!this.isGridUsable()) {
              return
            }
            let defaultItem = null
            const defaultConfigName = `local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`
            for (const item of res.data) {
              if (item.configName === defaultConfigName) {
                defaultItem = item
                this.setSummaryModesSummaryModes(defaultItem.configContent)
                continue
              }
              localStorage.setItem(item.configName, item.configContent)
            }
            if (defaultItem) {
              const { configContent, updateTime } = defaultItem
              let isLocal = false
              if (localColumnState && localUpdateTime && (new Date(updateTime).getTime() < Number(localUpdateTime))) {
                this.defaultColumnState = localColumnState
                isLocal = true
              } else {
                this.defaultColumnState = configContent
                localStorage.setItem(
                  `local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`,
                  configContent
                )
                localStorage.setItem(
                  `local_uiDoc_person_gridStatus_update_time_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`,
                  `${new Date(updateTime).getTime()}`
                )
              }
              if (isLocal) {
                this.saveState()
              }
            }
          }).catch(() => {
            if (localColumnState) {
              this.defaultColumnState = localColumnState
            }
          })
          if (!this.isGridUsable()) {
            return
          }
          this.refreshColumnState()
        }
      }
    },
    refreshColumnState(useCache = false) {
      if (!this.isGridUsable()) {
        return
      }
      const currentUserInfo = getUserInfo()
      // 取方案对应列状态
      if (this.perConfigName) {
        if (useCache) {
          let perColumnState = localStorage.getItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}_${this.perConfigName}_temp`)
          if (!perColumnState) {
            perColumnState = localStorage.getItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}_${this.perConfigName}`)
          }
          if (perColumnState) {
            this.useDefaultColumnState = false
            this.setColumnWidthAndState(this.handleColumnState(JSON.parse(perColumnState)))
            return
          }
        } else {
          localStorage.removeItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}_${this.perConfigName}_temp`)
          const perColumnState = localStorage.getItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}_${this.perConfigName}`)
          if (perColumnState) {
            this.useDefaultColumnState = false
            // this.params.columnApi.setColumnState(JSON.parse(perColumnState))
            this.setColumnWidthAndState(this.handleColumnState(JSON.parse(perColumnState)))
            return
          }
        }
      }
      /* 取默认列状态 */
      let defaultColumnState = localStorage.getItem(`local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`)
      if (!defaultColumnState) {
        defaultColumnState = localStorage.getItem(`local_uiDoc_init_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`)
      }
      this.useDefaultColumnState = true
      if (defaultColumnState) {
        // this.params.columnApi.setColumnState(JSON.parse(defaultColumnState))
        this.setColumnWidthAndState(this.handleColumnState(JSON.parse(defaultColumnState)))
      }
    },
    handleColumnState(columnState) {
      let newColumnState = columnState
      if (typeof this.preColumnStateCallback === 'function') {
        newColumnState = this.preColumnStateCallback(newColumnState)
      }
      return this.handleRowGroup(newColumnState)
    },
    handleRowGroup(columnState) {
      if (!this.existCustomGroup && !this.usedRowGroup) {
        for (let i = columnState.length - 1; i >= 0; --i) {
          if (columnState[i].colId === 'ag-Grid-AutoColumn') {
            columnState.splice(i, 1)
          } else if (columnState[i].rowGroup) {
            columnState[i].rowGroup = null
            columnState[i].rowGroupIndex = null
          }
        }
      }
      return columnState
    },
    setInitColumnStatus(params) {
      const currentUserInfo = getUserInfo()
      localStorage.setItem(
        `local_uiDoc_init_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`,
        JSON.stringify(params.columnApi.getColumnState())
      )
    },
    // 右击菜单列表
    getContextMenuItems(params) {
      if (!this.showMenuItems) {
        return []
      }
      const self = this
      let menus = [
        {
          name: i18n.t('CargoMsg.Cargo_Copy'),
          action: function() {
            const textarea = document.createElement('textarea')
            document.body.appendChild(textarea)
            const exportRows = dataConvert([params.node.data], [params.column]).exportRows
            textarea.textContent = exportRows && exportRows.length > 0 ? exportRows[0][params.column.colDef.field] : ''
            textarea.select()
            if (document.execCommand('copy')) {
              document.execCommand('copy')
            }
            document.body.removeChild(textarea)
          }
        },
        {
          name: `${i18n.t('CargoMsg.Cargo_Copy')} ${params.column ? params.column.colDef.headerName : ''}`,
          action: function() {
            const selectedRows = params.api.getModel().rowsToDisplay.filter(node => node.selected).map(node => node.data)
            let exportRows = null
            if (selectedRows.length) {
              exportRows = dataConvert(selectedRows, [params.column]).exportRows
            } else {
              exportRows = dataConvert([params.node.data], [params.column]).exportRows
            }
            let text = ''
            exportRows.forEach(item => {
              if (text) {
                text += '\n'
              }
              text += item[params.column.colDef.field]
            })
            let textarea = document.createElement('textarea')
            document.body.appendChild(textarea)
            textarea.textContent = text
            textarea.select()
            if (document.execCommand('copy')) {
              document.execCommand('copy')
            }
            document.body.removeChild(textarea)
            textarea = null
          }
        },
        {
          name: `${i18n.t('SeaExport.ExportToExcel')}(${i18n.t('Message.CurrentSelection')})`,
          action: function() {
            self.handleExportSelectedToExcel(params)
          }
        },
        {
          name: `${i18n.t('SeaExport.ExportToExcel')}(${i18n.t('Message.CurrentPage')})`,
          action: function() {
            self.handleCurPageExportToExcel(params)
          }
        },
        {
          name: `${i18n.t('CargoMsg.Cargo_DataExportPDF')}(${i18n.t('Message.CurrentSelection')})`,
          action: function() {
            self.handleExportSelectedToPdf(params)
          }
        },
        {
          name: `${i18n.t('CargoMsg.Cargo_DataExportPDF')}(${i18n.t('Message.CurrentPage')})`,
          action: function() {
            self.handleCurPageExportToPdf(params)
          }
        },
        {
          name: i18n.t('Message.ColumnSettings'),
          action: function() {
            params.api.openToolPanel('columns')
          }
        }
      ]
      if (typeof this.filterMenuItems === 'function') {
        menus = this.filterMenuItems(menus)
      }
      const hideAllExports = this.$checkPermission(['role_hideAllExports']) && !this.$store.getters.isAdmin
      if (!this.hasExportRole || hideAllExports) {
        const newMenus = []
        for (const menu of menus) {
          if (typeof menu === 'object' && (menu.name.indexOf(i18n.t('SeaExport.ExportToExcel')) !== -1 || menu.name.indexOf(i18n.t('CargoMsg.Cargo_DataExportPDF')) !== -1)) {
            continue
          }
          newMenus.push(menu)
        }
        if (newMenus.length > 0) return newMenus
      }
      return menus
    },
    getMainMenuItems(params) {
      const menu = [
        'pinSubMenu',
        { name: this.$t('grid.pinLeftAll'), action: () => this.pinLeft(params) },
        { name: this.$t('grid.autoSizeColumns'), action: () => this.autoSizeColumns(params) },
        { name: this.$t('grid.resetSort'), action: () => { this.resetSort(params) } },
        { name: this.$t('grid.restColumns'), action: () => { this.resetState(params) } }
      ]
      // 字段分组模式添加合计菜单
      if (this.customMenuItems && this.usedRowGroup) {
        menu.push(...this.customMenuItems.summaryModes.setMenus(params))
      }
      return menu
    },
    // 右击菜单列表-导出Excel(当前选择)
    handleExportSelectedToExcel(params) {
      const { api, columnApi } = params
      // 获取显示列
      const allDisplayedColumns = columnApi.getAllDisplayedColumns()
      // 获取选中行
      let rowsToDisplay = []
      api.forEachNode(node => {
        if (node.selected) {
          rowsToDisplay.push(node)
        }
      })
      if (this.gridOptions.rowGroupPanelShow === 'always' && columnApi.getRowGroupColumns().length > 0) {
        rowsToDisplay = this.getRelatedParentNodes(rowsToDisplay)
      }
      repareNodeSort(rowsToDisplay)
      if (this.gridOptions.rowGroupPanelShow === 'always' && columnApi.getRowGroupColumns().length > 0) {
        this.handleGroupNode(allDisplayedColumns, rowsToDisplay)
      }
      const rows = rowsToDisplay.map(item => item.data).filter(data => data !== undefined)
      if (rows.length === 0) {
        if (i18n) {
          this.$message.error(i18n.t('Message.NoDataIsCurrentlySelected'))
        } else {
          this.$message.error(this.$t('Message.NoDataIsCurrentlySelected'))
        }
        return
      }
      this.loading = getLoading()
      setTimeout(() => {
        const fileName = this.getFileName()
        const { totalConfig } = this
        if (totalConfig) {
          totalConfig.forEach(item => {
            item.dataProvider = rows
            const charge = new ChargeCurrencyTotalGroup(item)
            charge.recalc()
          })
        }
        exportExcel(fileName, rows, allDisplayedColumns, true, totalConfig)
        this.loading.close()
      }, 100)
    },
    // 获取关联的父级节点
    getRelatedParentNodes(nodes) {
      const result = []
      const parentNodeSet = new Set()
      for (let node of nodes) {
        const subNodes = [node]
        while (node.parent && node.parent.groupData) {
          node = node.parent
          if (!parentNodeSet.has(node)) {
            subNodes.push(node)
            parentNodeSet.add(node)
          }
        }
        for (let i = subNodes.length - 1; i >= 0; --i) {
          result.push(subNodes[i])
        }
      }
      return result
    },
    // 右击菜单列表-导出Excel(当前页)
    handleCurPageExportToExcel(params) {
      const { api, columnApi } = params
      // 获取显示列
      const allDisplayedColumns = columnApi.getAllDisplayedColumns()
      // 获取当前显示行
      let rowsToDisplay = []
      api.forEachNode(node => {
        if (node.displayed) {
          rowsToDisplay.push(node)
        }
      })
      rowsToDisplay = this.getRelatedChildNodes(rowsToDisplay)
      repareNodeSort(rowsToDisplay)
      if (this.gridOptions.rowGroupPanelShow === 'always' && columnApi.getRowGroupColumns().length > 0) {
        this.handleGroupNode(allDisplayedColumns, rowsToDisplay)
      }
      const rows = rowsToDisplay.map(item => item.data).filter(data => data !== undefined)
      if (rows.length === 0) {
        if (i18n) {
          this.$message.error(i18n.t('Message.NoDataIsCurrentlySelected'))
        } else {
          this.$message.error(this.$t('Message.NoDataIsCurrentlySelected'))
        }
        return
      }
      this.loading = getLoading()
      setTimeout(() => {
        const fileName = this.getFileName()
        const { totalConfig } = this
        if (totalConfig) {
          totalConfig.forEach(item => {
            item.dataProvider = rows
            const charge = new ChargeCurrencyTotalGroup(item)
            charge.recalc()
          })
        }
        exportExcel(fileName, rows, allDisplayedColumns, true, totalConfig)
        this.loading.close()
      }, 100)
    },
    // 获取关联的子级节点
    getRelatedChildNodes(nodes) {
      const result = []
      const nodeSet = new Set()
      for (const node of nodes) {
        const subNodes = this.getSingelRelatedChildNode(node)
        for (const subNode of subNodes) {
          if (!nodeSet.has(subNode)) {
            result.push(subNode)
            nodeSet.add(subNode)
          }
        }
      }
      return result
    },
    // 获取(单个节点下)关联的子级节点
    getSingelRelatedChildNode(node) {
      if (node.childrenAfterFilter && node.childrenAfterFilter.length > 0) {
        return [node, ...this.getRelatedChildNodes(node.childrenAfterFilter)]
      }
      return [node]
    },
    // 处理分组数据
    handleGroupNode(allDisplayedColumns, rowsToDisplay) {
      for (let i = 0; i < allDisplayedColumns.length; ++i) {
        const column = allDisplayedColumns[i]
        if (column.colId === 'ag-Grid-AutoColumn') {
          const newColumn = { ...column }
          newColumn.colDef = { ...newColumn.colDef, field: '_groupValue', excelRenderer: params => params.value }
          allDisplayedColumns[i] = newColumn
        } else {
          const newColumn = { ...column }
          const colDef = newColumn.colDef
          const newColDef = { ...colDef }
          if (colDef.excelRenderer && typeof colDef.excelRenderer === 'function') {
            newColDef.excelRenderer = params => {
              if (params.data.hasOwnProperty('_groupValue')) {
                return params.value || ''
              }
              return colDef.excelRenderer(params)
            }
          } else if (colDef.cellRenderer && typeof colDef.cellRenderer === 'function') {
            newColDef.cellRenderer = params => {
              if (params.data.hasOwnProperty('_groupValue')) {
                return params.value || ''
              }
              return colDef.cellRenderer(params)
            }
          } else if (colDef.valueFormatter && typeof colDef.valueFormatter === 'function') {
            newColDef.valueFormatter = params => {
              if (params.data.hasOwnProperty('_groupValue')) {
                return params.value || ''
              }
              return colDef.valueFormatter(params)
            }
          } else if (colDef.valueGetter && typeof colDef.valueGetter === 'function') {
            newColDef.valueGetter = params => {
              if (params.data.hasOwnProperty('_groupValue')) {
                return params.value || ''
              }
              return colDef.valueGetter(params)
            }
          }
          newColumn.colDef = newColDef
          allDisplayedColumns[i] = newColumn
        }
      }
      for (let i = 0; i < rowsToDisplay.length; ++i) {
        const node = rowsToDisplay[i]
        if (node.group) {
          const newNode = { ...node }
          const data = newNode.aggData ? { ...newNode.aggData } : {}
          const value = newNode.groupData['ag-Grid-AutoColumn']
          const colDef = newNode.rowGroupColumn.colDef
          let _groupValue = value
          if (colDef.excelRenderer && typeof colDef.excelRenderer === 'function') {
            _groupValue = colDef.excelRenderer({ data, value })
          } else if (colDef.cellRenderer && typeof colDef.cellRenderer === 'function') {
            _groupValue = colDef.cellRenderer({ data, value })
          } else if (colDef.valueFormatter && typeof colDef.valueFormatter === 'function') {
            _groupValue = colDef.valueFormatter({ data, value })
          } else if (colDef.valueGetter && typeof colDef.valueGetter === 'function') {
            _groupValue = colDef.valueGetter({ data, value })
          }
          data._groupValue = _groupValue + `(${node.allChildrenCount})`
          newNode.data = data
          rowsToDisplay[i] = newNode
        }
      }
    },
    // 右击菜单列表-导出PDF(当前选择)
    handleExportSelectedToPdf(params) {
      const { api, columnApi } = params
      // 获取显示列
      const allDisplayedColumns = columnApi.getAllDisplayedColumns()
      // 获取选中行
      let rowsToDisplay = []
      api.forEachNode(node => {
        if (node.selected) {
          rowsToDisplay.push(node)
        }
      })
      if (this.gridOptions.rowGroupPanelShow === 'always' && columnApi.getRowGroupColumns().length > 0) {
        rowsToDisplay = this.getRelatedParentNodes(rowsToDisplay)
      }
      repareNodeSort(rowsToDisplay)
      if (this.gridOptions.rowGroupPanelShow === 'always' && columnApi.getRowGroupColumns().length > 0) {
        this.handleGroupNode(allDisplayedColumns, rowsToDisplay)
      }
      const rows = rowsToDisplay.map(item => item.data).filter(data => data !== undefined)
      if (rows.length === 0) {
        if (i18n) {
          this.$message.error(i18n.t('Message.NoDataIsCurrentlySelected'))
        } else {
          this.$message.error(this.$t('Message.NoDataIsCurrentlySelected'))
        }
        return
      }
      this.loading = getLoading()
      setTimeout(() => {
        const fileName = this.getFileName()
        exportPdf(fileName, rows, allDisplayedColumns)
        this.loading.close()
      }, 100)
    },
    getFileName() {
      const date = new Date()
      const month = date.getMonth() + 1
      const hour = date.getHours()
      const min = date.getMinutes()
      const sec = date.getSeconds()
      return this.exportFileName || `R${month}${hour}${min}${sec}`
    },
    // 右击菜单列表-导出PDF(当前页)
    handleCurPageExportToPdf(params) {
      this.loading = getLoading()
      setTimeout(() => {
        const { api, columnApi } = params
        // 获取显示列
        const allDisplayedColumns = columnApi.getAllDisplayedColumns()
        const fileName = this.getFileName()
        // 获取当前显示行
        let rowsToDisplay = []
        api.forEachNode(node => {
          if (node.displayed) {
            rowsToDisplay.push(node)
          }
        })
        rowsToDisplay = this.getRelatedChildNodes(rowsToDisplay)
        repareNodeSort(rowsToDisplay)
        if (this.gridOptions.rowGroupPanelShow === 'always' && columnApi.getRowGroupColumns().length > 0) {
          this.handleGroupNode(allDisplayedColumns, rowsToDisplay)
        }
        const rows = rowsToDisplay.map(item => item.data)
        exportPdf(fileName, rows, allDisplayedColumns)
        this.loading.close()
      }, 100)
    },
    saveLocalSate(params) {
      if (this.perConfig.configModule) {
        const currentUserInfo = getUserInfo()
        const configName = `local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`
        const configUpdateAtName = `local_uiDoc_person_gridStatus_update_time_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`
        const configContent = JSON.stringify(params.columnApi.getColumnState())
        if (configContent === this.defaultColumnState) {
          return
        }
        localStorage.setItem(
          configName,
          configContent
        )
        localStorage.setItem(
          configUpdateAtName,
          `${new Date().getTime()}`
        )
      }
    },
    saveState(params) {
      if (this.perConfig.configModule) {
        const currentUserInfo = getUserInfo()
        const configName = `local_uiDoc_person_gridStatus_${currentUserInfo.tenantId}_${currentUserInfo.userId}_${this.configModule}`
        const configContent = localStorage.getItem(configName)
        const data = {
          ...this.perConfig,
          configName,
          configContent,
          configBy: currentUserInfo.userId
        }

        if (this.customMenuItems?.summaryModes.configContent) {
          // 确保 configContent 是一个对象
          const parsedConfigContent = JSON.parse(configContent || '{}')

          // 更新 configContent 数据，映射每个条目并添加汇总模式
          const updatedConfigContent = parsedConfigContent.map(item => ({
            ...item,
            [this.customMenuItems.summaryModes.key]: this.customMenuItems.summaryModes.configContent[item.colId] || null
          }))

          // 更新 data 的 configContent
          data.configContent = JSON.stringify(updatedConfigContent)
        }

        if (data.configContent === this.defaultColumnState) {
          return
        }
        savePersonQueryConfig(data).then(res => {
          this.defaultColumnState = configContent
          this.$emit('saveColumnState')
        }).catch((err) => {
          if (this.$logger && typeof this.$logger.error === 'function') {
            this.$logger.error(err)
          }
          this.$message.error(this.$t('Message.SaveColumnStatusFailed') + this.configModule)
          this.$once('hook:beforeDestroy', () => {
            this.saveState()
          })
          window.addEventListener('beforeunload', this.saveState)
        })
      }
    },
    resetSort(params) {
      /* 重置排序信息 */
      this.oldSortList = []
      this.nowSortList = []
      this.currentColumn = ''
      const columnState = params.columnApi.getColumnState()
      for (const item of columnState) {
        item.sort = null
        item.sortIndex = null
        if (this.cancelSortAutoReduce) {
          const headerWidth = this.getHeaderWidth(item)
          if (item.width > headerWidth) {
            let newWidth = item.width - 20
            if (newWidth < headerWidth) {
              newWidth = headerWidth
            }
            item.width = newWidth
          }
        }
      }
      // this.params.columnApi.setColumnState(columnState)
      this.setColumnWidthAndState(columnState)
    },
    resetSort2(params) {
      const oldColumnState = this.oldSortList
      /* 重置排序信息 */
      this.oldSortList = []
      this.nowSortList = []
      this.currentColumn = ''
      const columnState = params.columnApi.getColumnState()
      for (const item of columnState) {
        item.sort = null
        item.sortIndex = null
      }
      this.params.columnApi.setColumnState(columnState)
      return oldColumnState
    },
    setListSort(params, oldSort) {
      if (!oldSort) {
        return
      }
      this.oldSortList = []
      this.currentColumn = ''
      const columnState = params.columnApi.getColumnState()
      for (const item of columnState) {
        const sort = oldSort.find(p => p.colId === item.colId)
        if (sort) {
          item.sort = sort.sort
          item.sortIndex = sort.sortIndex
          this.oldSortList.push(item)
        }
      }
      this.nowSortList = columnState
      this.params.columnApi.setColumnState(columnState)
    },
    resetState(params) {
      /* 重置排序信息 */
      this.oldSortList = []
      this.nowSortList = []
      this.currentColumn = ''
      // 从缓存中获取
      const showColumnDefs = this.columnDefs.filter(item => !item.suppressColumnsToolPanel) // 过滤掉隐藏的列
      const hideColumnDefs = this.columnDefs.filter(item => item.suppressColumnsToolPanel).map(item => item.colId) // 获取需要隐藏的列
      const colIds = showColumnDefs.map(item => ({ key: item.colId, newWidth: item.initialWidth || item.width }))
      params.api.setColumnDefs(this.columnDefs)
      params.columnApi.setColumnWidths(colIds)
      params.columnApi.resetColumnState()
      params.columnApi.setColumnsVisible(hideColumnDefs, false)
      // 重置统计汇总方式
      if (this.customMenuItems) {
        const columnStateStr = JSON.stringify(params.columnApi.getColumnState())
        this.customMenuItems.summaryModes.configContent = {}
        this.setSummaryModesSummaryModes(columnStateStr)
      }
    },
    rowDragEndCallback(e) {
      const tags = document.getElementsByClassName('ag-dnd-ghost')
      if (tags.length !== 0) {
        tags.forEach(tag => {
          tag.parentNode.removeChild(tag)
        })
      }
      this.$emit('rowDragEnd', e)
      /* console.log('befordragEnd', e.columnApi.getColumn(this.rowIndexName))
      if (e.columnApi.getColumn(this.rowIndexName)) {
        e.api.forEachNode((node, index) => {
          console.log('rowDragEndCallback', node, index)
          node.setDataValue(this.rowIndexName, index + 1)
        })
        e.api.flashCells({ columns: [this.rowIndexName] })
      }*/
    },
    toolPanelEnterListener() {
      if (this.closeToolPanelTimerId > 0) {
        clearTimeout(this.closeToolPanelTimerId)
        this.closeToolPanelTimerId = 0
      }
      window.removeEventListener('click', this.clickToolPanelOther)
    },
    toolPanelListener() {
      const { api } = this.params
      this.closeToolPanelTimerId = setTimeout(() => {
        this.closeToolPanelTimerId = 0
        if (api.getOpenedToolPanel()) {
          api.closeToolPanel()
        }
      }, 3000)
      window.addEventListener('click', this.clickToolPanelOther)
    },
    clickToolPanelOther(e) {
      const grid = this.getGridRootEl()
      if (grid) {
        const toolPanelWarpper = grid.querySelector('.ag-tool-panel-wrapper')
        if (toolPanelWarpper && !toolPanelWarpper.contains(e.target)) {
          const { api } = this.params
          if (api.getOpenedToolPanel()) {
            api.closeToolPanel()
          }
          window.removeEventListener('click', this.clickToolPanelOther)
        }
      } else {
        window.removeEventListener('click', this.clickToolPanelOther)
      }
    },
    toolPaneInputStartListener() {
      const grid = this.getGridRootEl()
      if (!grid) {
        return
      }
      const toolPanelWrapper = grid.querySelector('.ag-tool-panel-wrapper')
      if (toolPanelWrapper) {
        toolPanelWrapper.removeEventListener('mouseenter', this.toolPanelEnterListener)
        toolPanelWrapper.removeEventListener('mouseleave', this.toolPanelListener)
      }
    },
    toolPaneInputEndListener() {
      const grid = this.getGridRootEl()
      if (!grid) {
        return
      }
      const toolPanelWrapper = grid.querySelector('.ag-tool-panel-wrapper')
      if (toolPanelWrapper) {
        toolPanelWrapper.addEventListener('mouseenter', this.toolPanelEnterListener)
        toolPanelWrapper.addEventListener('mouseleave', this.toolPanelListener)
      }
    },
    displayedColumnsChanged(e) {
      // console.log('displayedColumnsChanged', e)
      // console.log('columnState', e.columnApi.getColumnState())
    },
    // 设置全局数据列
    setColumnDefs() {
      if (!this.columnDefs || !this.isGridUsable()) {
        return
      }
      const withColId = this.columnDefs.map((item, index) => {
        if (item.checkboxSelection) {
          if (item.headerName) {
            item.minWidth = 60
          } else {
            if (item.rowDrag) {
              item.maxWidth = 60
            } else {
              // 复选框单独成列的情况配置
              item.maxWidth = 40
              // CARGO-19255 复选框单独成列需配置列菜单
              item.suppressMenu = false
              item.filter = false
            }
          }
        } else {
          item.minWidth = item.minWidth || 70
        }
        if (item.hasOwnProperty('width')) {
          item.initialWidth = item.width
        }
        if (item.hasOwnProperty('hide')) {
          item.initialHide = item.hide
          delete item.hide
        }
        if (!item.colId) {
          if (item.field) {
            item.colId = item.field
          }
          if (!item.field) {
            item.colId = `custom${index}`
          }
        } else {
          delete item.width
        }
        if (!item.comparator) {
          let valueConvertor = null
          if (typeof item.valueFormatter === 'function') {
            valueConvertor = item.valueFormatter
          } else if (typeof item.cellRenderer === 'function') {
            valueConvertor = item.cellRenderer
          }
          item.comparator = (a, b, nodeA, nodeB) => {
            const originA = a
            const originB = b
            if (valueConvertor) {
              if (nodeA && nodeB) {
                if (this.enableQuickValueConvert && this.valueConvertMap.has(`${item.field}-${a}`)) {
                  a = this.valueConvertMap.get(`${item.field}-${a}`)
                } else {
                  a = valueConvertor({ value: a, data: nodeA.data, node: nodeA, rowIndex: nodeA.rowIndex, colDef: item })
                }
                if (this.enableQuickValueConvert && this.valueConvertMap.has(`${item.field}-${b}`)) {
                  b = this.valueConvertMap.get(`${item.field}-${b}`)
                } else {
                  b = valueConvertor({ value: b, data: nodeB.data, node: nodeB, rowIndex: nodeB.rowIndex, colDef: item })
                }
              }
            }
            // if (a && typeof a === 'string') {
            //   a = a.replaceAll(',', '')
            // }
            // if (b && typeof b === 'string') {
            //   b = b.replaceAll(',', '')
            // }
            // // 当两者都是数字的时候，才进行转数字处理
            // if (a && isNumber2(a) && b && isNumber2(b)) {
            //   a = Number(a)
            //   b = Number(b)
            // }
            if (typeof a === 'string' && typeof b === 'string') {
              if (a && b && typeof originA === 'number' && typeof originB === 'number') {
                const aNum = this.formatTextToNumber(a)
                const bNum = this.formatTextToNumber(b)
                if (!isNaN(aNum) && !isNaN(bNum) && Math.abs(aNum - originA) < 1 && Math.abs(bNum - originB) < 1) {
                  return aNum - bNum
                }
              }
              for (let i = 0; i < a.length && i < b.length; ++i) {
                const aCode = a.charCodeAt(i)
                const bCode = b.charCodeAt(i)
                const diff = aCode >= 256 && bCode >= 256 ? a[i].localeCompare(b[i], 'zh') : aCode - bCode
                if (diff !== 0) {
                  return diff
                }
              }
              return a.length - b.length
            }
            if (a === b) {
              return 0
            } else if (a < b) {
              return -1
            } else {
              return 1
            }
          }
        }
        // 设置列筛选功能的过滤器
        if (!item.filterParams) {
          if (item.valueFormatter) {
            item.filterParams = {
              showTooltips: true,
              valueFormatter: (params) => {
                if (params.value === this._i18nGridLocalText('selectAll')) {
                  return params.value
                } else {
                  if (params.value === 'false' || params.value === 'true') {
                    params.value = JSON.parse(params.value)
                  } else if (params.value === null) {
                    const isNum = Number(params.value)
                    params.value = isNaN(isNum) ? params.value : isNum
                  } else {
                    params.value = stringToNum(params.value)
                  }
                  const resault = item.valueFormatter(params)
                  return resault === '' ? this.$t('Message.blank') : resault + ''
                }
              }
            }
          } else if (item.cellRenderer) {
            item.filterParams = {
              showTooltips: true,
              valueFormatter: (params) => {
                if (params.value === this._i18nGridLocalText('selectAll')) {
                  return params.value
                } else {
                  if (params.value === 'false' || params.value === 'true') {
                    params.value = JSON.parse(params.value)
                  } else if (params.value === null) {
                    const isNum = Number(params.value)
                    params.value = isNaN(isNum) ? params.value : isNum
                  } else {
                    params.value = stringToNum(params.value)
                  }
                  const resault = item.cellRenderer(params)
                  return resault === '' ? this.$t('Message.blank') : resault + ''
                }
              }
            }
          } else {
            // 对没有特殊过滤的列，且值为空格的进行数据处理
            item.filterParams = {
              showTooltips: true,
              valueFormatter: (params) => {
                if (params.value) {
                  return params.value.trim() === '' ? this.$t('Message.blank') : params.value
                }
                return params.value
              }
            }
          }
        }
        // 设置全选根据筛选结果选中
        if (item.headerCheckboxSelection) {
          item.headerCheckboxSelectionFilteredOnly = true
        }
        return item
      })
      this.params.api.setColumnDefs(withColId)
    },
    /**
     * （数字）格式化后的文本转为数字
     */
    formatTextToNumber(text) {
      text = text.replaceAll(',', '')
      if (isNumber2(text)) {
        return Number(text)
      }
      return NaN
    },
    getSelectionText() {
      let text = ''
      if (window.getSelection) {
        try {
          const activeElement = document.activeElement
          if (activeElement && activeElement.value) {
            // firefox bug https://bugzilla.mozilla.org/show_bug.cgi?id=85686
            text = activeElement.value.substring(activeElement.selectionStart, activeElement.selectionEnd)
          } else {
            const selection = window.getSelection()
            text = selection.type !== 'None' ? selection.toString() : activeElement.textContent
          }
        } catch (e) {
          text = ''
        }
      } else if (document.selection && document.selection.type !== 'Control') {
        // For IE
        text = document.selection.createRange().text
      }
      if (text.endsWith('\n')) {
        text = text.substring(0, text.length - 1)
      }
      return text
    },
    onProcessCellForClipboard: lodash.debounce(function(params) {
      const { colDef } = params.column
      if (colDef.cellRendererFramework && colDef.custom && colDef.custom.isEdit) {
        const { custom } = colDef
        if (colDef.custom.tag === 'cargo-advance-select') {
          return ColumnUtils.resolveColumnLabel(custom.selectType, custom.tagValue, params.value, custom.tagName)
        }
        if (colDef.custom.tag === 'cargo-cell-select') {
          const options = colDef.custom.attrs.cellSelectAttrs.fullOptions
          if (options) {
            const selected = options.find(p => p[colDef.custom.optionField.value] === params.value)
            if (selected) {
              return selected[colDef.custom.optionField.label]
            }
          }
        }
      }
      if (colDef.custom && colDef.custom.tag === 'router-and-print') {
        return params.value
      }
      return this.getSelectionText()
    }, 500, {
      leading: true
    }),
    pinLeft(e) {
      const { column: { colId }, columnApi } = e
      const columns = columnApi.getAllDisplayedColumns()
      const index = columns.findIndex(item => item.colId === colId)
      const colIds = columns.filter((item, i) => i <= index).map(item => item.colId)
      columnApi.setColumnsPinned(colIds, 'left')
    },
    stateMatch(params) {
      const { defaultColumnState } = this
      return !defaultColumnState || JSON.parse(defaultColumnState).length <= params.columnApi.getColumnState().length
    },
    // 更新快速值转换的映射
    updateValueConvertMap() {
      if (!this.enableQuickValueConvert || !this.columnDefs) {
        this.valueConvertMap.clear()
        return
      }
      const oldKeySet = new Set(this.valueConvertMap.keys())
      const newKeySet = new Set()
      for (const colDef of this.columnDefs) {
        if (!colDef.field) {
          continue
        }
        const field = colDef.field
        let valueConvertor = null
        if (typeof colDef.valueFormatter === 'function') {
          valueConvertor = colDef.valueFormatter
        } else if (typeof colDef.cellRenderer === 'function') {
          valueConvertor = colDef.cellRenderer
        }
        if (!valueConvertor) {
          continue
        }
        for (const row of this.rowData) {
          const originValue = row[field]
          const key = `${field}-${originValue}`
          if (newKeySet.has(key)) {
            continue
          }
          if (oldKeySet.has(key)) {
            newKeySet.add(key)
            continue
          }
          this.valueConvertMap.set(key, valueConvertor({ value: originValue, data: row }))
          newKeySet.add(key)
        }
      }
      for (const key of oldKeySet) {
        if (!newKeySet.has(key)) {
          this.valueConvertMap.delete(key)
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import "~@/styles/element-variables.scss";
.cargo-ag-grid {
  display: flex;width: 100%;flex:1;
  &.check-background-unset {
    .ag-theme-balham {
      .ag-row-selected {
        &.ag-row-even {
          background-color: #fff !important;
          &.ag-row-hover {
            background-color: #9bddff ;
          }
          &.ag-row-focus, &.ag-row-hover.ag-row-focus, &.shift-select, &.ag-row-hover.shift-select, &.ctrl-select, &.ag-row-hover.ctrl-select  {
            background-color: #6abdff ;
          }
        }
        &.ag-row-odd {
          background-color: #F7F7F7 !important;
          &.ag-row-hover {
            background-color: #9bddff ;
          }
          &.ag-row-focus, &.ag-row-hover.ag-row-focus, &.shift-select, &.ag-row-hover.shift-select, &.ctrl-select, &.ag-row-hover.ctrl-select {
            background-color: #6abdff ;
          }
        }
      }
    }
  }
  .ag-theme-balham {
    .ag-row{
      border:none!important;
    }
    .ag-cell {
      user-select: text;
      border-right: 1px solid #DBDFE6;
    }
    &.showSelectedColor {
      .ag-row-selected {
        background-color: #9bddff !important;
      }
    }
    .ag-row-selected {
      background-color: #9bddff ;
      /*background-color: #f2f2f2;*/
      &.ag-row-focus, &.shift-select, &.ctrl-select {
        background-color: #6abdff !important;
      }
    }

    .ag-row-hover {
      background-color: #9bddff !important;
    }
    .ag-row-focus, .ag-row-hover.ag-row-focus, .shift-select, .ag-row-hover.shift-select, .ctrl-select, .ag-row-hover.ctrl-select {
      background-color: #6abdff !important;
    }

    .ag-column-panel {
      min-height: 50px !important;
    }
    .ag-row-even{
      background-color: #fff;
    }
    .ag-row-odd{
      background-color: #F7F7F7;
    }
    .ag-header-cell{
      padding-left: 0;
      padding-right: 0;
    }
    .ag-header-select-all{
      position: absolute;
      margin-left: 6px;
      margin-right: 0;
    }
    .ag-cell-label-container{
      padding-left: 6px;
      .ag-sort-order {
        margin-left: -15px !important;
      }
      .ag-header-cell-label{
        text-indent: 20px;
        .ag-icon-asc, .ag-icon-desc {
          width: 0;
          &:before {
            margin-left: -25px;
          }
        }
      }
    }
    .ag-cell{
      padding-left: 5px!important;
    }
    .ag-cell.ag-cell-inline-editing{
      padding: 0!important;
      border:none!important;
    }
    .ag-ltr .ag-row-group-leaf-indent {
      margin-left: 10px!important;
    }
    .ag-cell .ag-icon.ag-icon-contracted{
      margin-right: 5px!important;
    }
    .ag-pinned-left-cols-viewport {
      background-color: $--background-color-base;
      overflow: hidden!important;
    }
    .clear-detail .clear-history-row{
      background-color: #ffd383;
    }
    div.ag-theme-balham > div.ag-menu {
      z-index: 9999;
    }
    .ag-body-viewport.ag-layout-normal {
      background-color: $--background-color-base;
    }

    .ag-header-row {
      border-style: solid;
      border-color: #DCDFE6;
      box-sizing: border-box;
      border-width: 0 0 1px; }
    .ag-root {
      border: 1px solid #DCDFE6; }
    .ag-root-wrapper{
      border:none
    }
    .ag-header {
      color: rgba(51,51, 51,.8)!important;
      border:none
    }
    .ag-side-bar {
      border-left: none;
    }
    .ag-side-bar .ag-side-buttons {
      padding-top: 16px;
      background: #F0F3F8;
      border: 1px solid #DCDFE6;
      border-left: none;
      position: relative; }
    .ag-checkbox-input-wrapper {
      position: relative;
      .ag-checkbox-input{
        position: absolute;
        top:0;
        left: 0;
      }
    }
    .ag-group-checkbox{
      white-space: normal;
    }
    .ag-cell-wrap-text{
      white-space: pre;
    }
    .ag-cell-wrapper {
      align-items: unset;
    }
    .ag-cell-last-left-pinned{
      .ag-cell-value {
        white-space: normal;
      }
    }
  }
}

.ag-theme-balham .ag-tab-header { // clumsy controller
  display: none !important;
}
.ag-dnd-ghost{
  z-index: 9999;
}
</style>
<style lang="scss" scoped>
/deep/ .ag-set-filter-body-wrapper{
  .ag-set-filter-list{
    .ag-filter-virtual-list-item{
      .ag-set-filter-item-checkbox{
        width: 100%;
        overflow: hidden;
        .ag-input-field-label{
          width: 100%;
          height: 24px;
          line-height: 24px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
}
</style>
